import {useMutation} from '@github-ui/react-query'
import type {
  AddReactionMutationMutation,
  AddReactionMutationMutationVariables,
} from '@github-ui/server-persisted-queries/types/addReactionMutation'
import type {Environment} from 'relay-runtime'

import {
  applyOptimisticReaction,
  completeReactionMutation,
  type ReactionMutationCallbacks,
  type ReactionMutationContext,
  startReactionMutation,
  submitReactionMutation,
  writeReactionSubjectToRelay,
} from './reaction-mutation'

type AddReactionSubject = NonNullable<NonNullable<AddReactionMutationMutation['addReaction']>['subject']>
type AddReactionVariables = AddReactionMutationMutationVariables & ReactionMutationCallbacks<AddReactionSubject>

export function useAddReactionMutation(environment: Environment) {
  return useMutation<AddReactionSubject, Error, AddReactionVariables, ReactionMutationContext>({
    mutationFn: async variables => {
      const data = await submitReactionMutation<AddReactionMutationMutation>('POST', variables)
      const subject = data.addReaction?.subject
      if (!subject) throw new Error('Add reaction mutation returned no subject')
      return subject
    },
    onMutate: variables => ({
      optimisticUpdate: applyOptimisticReaction(environment, variables, true),
      mutation: startReactionMutation(environment, String(variables.input.subjectId)),
    }),
    onSuccess: (subject, variables, context) => {
      context?.optimisticUpdate.dispose()
      if (!context || !completeReactionMutation(environment, String(variables.input.subjectId), context.mutation)) {
        variables.onStale?.()
        return
      }

      writeReactionSubjectToRelay(environment, subject)
      variables.onCompleted?.(subject)
    },
    onError: (error, variables, context) => {
      context?.optimisticUpdate.dispose()
      if (!context || !completeReactionMutation(environment, String(variables.input.subjectId), context.mutation)) {
        variables.onStale?.()
        return
      }

      variables.onError?.(error)
    },
  })
}
